import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import BackdropDialog from 'common/components/BackdropDialog';
import { Plan, PlanDefinitions } from 'common/modules/plans';
import { Typography } from '@mui/material';
import { useTranslation } from 'services/localization';
import { Callable } from 'common/frontend/callable';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { SubscriptionDurationOption } from '../utils';

export interface Props {
	isOpen: boolean;
	onClose: () => void;
	onCancel: () => void;
	subscriptionDuration: SubscriptionDurationOption;
	plan: Plan;
}

const RequestYearlyQuotaModal = (props: Props) => {
	const { t } = useTranslation();
	const { isOpen, onClose, onCancel, subscriptionDuration, plan } = props;
	const [isRequestSent, setIsRequestSent] = useState<boolean>(false);
	const [saving, setSaving] = useState<boolean>(false);
	const planDefinition = PlanDefinitions[plan ?? 'BASIC'];
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const shopName = useSelector(ShopSelectors.activeShopData)?.publicInfo.name;
	const userEmail = useSelector(UserSelectors.userData)?.email;

	const renderBody = isRequestSent ? (
		<>
			<Typography variant="h6" sx={{ mb: 3 }}>
				{`${t('plans.requestYearlyQuota.requestSent', 'Request sent')}!`}
			</Typography>
			<Typography variant="body1" sx={{ mb: 2 }}>
				{`${t('plans.requestYearlyQuota.thankYouForYourInterestInPlanOnSubscription', {
					planName: planDefinition.name,
					subscriptionName: subscriptionDuration.name.toLowerCase(),
					defaultValue:
						'Thank you for your interest in {{planName}} on a {{subscriptionName}} subscription',
				})}.`}
			</Typography>
			<Typography variant="body1">{`${t(
				'plans.requestYearlyQuota.weWillGetBackToYouShortly',
				`We'll get back to you shortly`,
			)}!`}</Typography>
		</>
	) : (
		<>
			<Typography variant="h6" sx={{ mb: 3 }}>
				{planDefinition.name}
			</Typography>
			<Typography variant="body1" sx={{ mb: 2 }}>
				{`${t('plans.requestYearlyQuota.sendRequestPlanOnSubscription', {
					planName: planDefinition.name,
					subscriptionName: subscriptionDuration.name.toLowerCase(),
					discountPercentage: subscriptionDuration.discount * 100,
					defaultValue:
						'Send a request to start using {{planName}} on a {{subscriptionName}} subscription and save {{discountPercentage}}%',
				})}!`}
			</Typography>
			<Typography variant="body1">
				{`${t(
					'plans.requestYearlyQuota.weWillGetBackWithinFewDays',
					`We'll get back to you within a few days to get everything up and running`,
				)}.`}
			</Typography>
		</>
	);

	const handleRequestQuote = async () => {
		setSaving(true);
		await Callable.plans.subscriptions.quotaRequest({
			merchantId: shopId,
			merchantName: shopName ?? '',
			plan,
			subscriptionName: subscriptionDuration.name,
			subscriptionDuration: subscriptionDuration.duration,
			userEmail: userEmail ?? '',
		});
		setSaving(false);
		setIsRequestSent(true);
	};

	return (
		<BackdropDialog
			isOpen={isOpen}
			onClose={onClose}
			label={t('plans.requestYearlyQuota.requestYearlyQuota', 'Request Yearly Quota')}
			body={renderBody}
			buttonFullWidth={isRequestSent ? true : false}
			confirm={
				isRequestSent
					? {
							label: t('common:actions.close', 'Close'),
							variant: 'text',
							callback: onClose,
					  }
					: {
							label: t('plans.requestYearlyQuota.requestQuota', 'Request Quota'),
							disableAutoClose: true,
							callback: handleRequestQuote,
							disabled: saving,
							loading: saving,
					  }
			}
			cancel={
				!isRequestSent
					? {
							label: t('common:actions.cancel', 'cancel'),
							callback: onCancel,
							disabled: saving,
					  }
					: undefined
			}
		/>
	);
};

export default RequestYearlyQuotaModal;
